import { timedFetch } from './net';
/**
 * Fetches several frames of one multi-frame instance in a single WADO-RS request
 * (`.../frames/1,2,3`) and hands each part to the pinned WADO loader's own decoder,
 * seeding cornerstone's cache exactly as a per-frame `wadors:` load would.
 * A 60-frame cine becomes 4 requests instead of 60.
 */

const MULTIPART_ACCEPT = 'multipart/related; type=application/octet-stream; transfer-syntax=*';
const FRAMES_PATH = /^wadors:(.+\/frames\/)(\d+)$/;
const DEFAULT_TRANSFER_SYNTAX = '1.2.840.10008.1.2.1';

export interface FrameRef {
  base: string;
  frame: number;
}

/** Splits a `wadors:` frame imageId into its instance frames URL and frame number. */
export function parseFrameImageId(imageId: string): FrameRef | null {
  const match = FRAMES_PATH.exec(imageId);
  return match ? { base: match[1], frame: Number(match[2]) } : null;
}

interface Part {
  data: Uint8Array;
  transferSyntax: string;
}

function indexOf(haystack: Uint8Array, needle: Uint8Array, from: number): number {
  outer: for (let i = from; i <= haystack.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) if (haystack[i + j] !== needle[j]) continue outer;
    return i;
  }
  return -1;
}

function transferSyntaxOf(headers: string, fallback: string): string {
  const match = /transfer-syntax="?([0-9.]+)/i.exec(headers);
  return match ? match[1] : fallback;
}

/** Parses a multipart/related body into its parts, in order. */
export function splitMultipart(body: Uint8Array, contentType: string): Part[] {
  const boundaryMatch = /boundary="?([^";]+)"?/i.exec(contentType);
  if (!boundaryMatch) throw new Error('multipart response without boundary');
  const encoder = new TextEncoder();
  const delimiter = encoder.encode(`--${boundaryMatch[1]}`);
  const headerEnd = encoder.encode('\r\n\r\n');
  const fallbackSyntax = transferSyntaxOf(contentType, DEFAULT_TRANSFER_SYNTAX);
  const decoder = new TextDecoder();
  const parts: Part[] = [];
  let cursor = indexOf(body, delimiter, 0);
  while (cursor >= 0) {
    const headersStart = cursor + delimiter.length;
    if (body[headersStart] === 0x2d && body[headersStart + 1] === 0x2d) break; // closing "--"
    const headersStop = indexOf(body, headerEnd, headersStart);
    if (headersStop < 0) break;
    const next = indexOf(body, delimiter, headersStop + headerEnd.length);
    if (next < 0) break;
    const dataEnd = next - 2; // strip the CRLF before the next delimiter
    const headers = decoder.decode(body.subarray(headersStart, headersStop));
    parts.push({ data: body.slice(headersStop + headerEnd.length, dataEnd), transferSyntax: transferSyntaxOf(headers, fallbackSyntax) });
    cursor = next;
  }
  return parts;
}

/**
 * Loads `frames` (same instance) with one request and puts each decoded frame in the cache.
 * Resolves once every frame is decoded; frames already cached are skipped.
 */
export async function loadFrameBatch(
  base: string,
  frames: number[],
  headers: Record<string, string>,
  signal: AbortSignal,
): Promise<void> {
  const { cornerstone, cornerstoneWADOImageLoader } = window;
  const wanted = frames.filter((frame) => !cornerstone.imageCache.getImageLoadObject(`wadors:${base}${frame}`));
  if (wanted.length === 0) return;
  const response = await timedFetch(`${base}${wanted.join(',')}`, {
    headers: { Accept: MULTIPART_ACCEPT, ...headers },
    credentials: 'same-origin',
    signal,
  });
  if (!response.ok) throw new Error(`frames ${response.status}`);
  const parts = splitMultipart(new Uint8Array(await response.arrayBuffer()), response.headers.get('content-type') ?? '');
  if (parts.length !== wanted.length) throw new Error(`expected ${wanted.length} frames, got ${parts.length}`);
  const decoded = wanted.map((frame, i) => {
    const imageId = `wadors:${base}${frame}`;
    const existing = cornerstone.imageCache.getImageLoadObject(imageId);
    if (existing) return existing.promise;
    const promise = cornerstoneWADOImageLoader.createImage(imageId, parts[i].data, parts[i].transferSyntax, {});
    cornerstone.imageCache.putImageLoadObject(imageId, { promise, cancelFn: undefined });
    return promise;
  });
  await Promise.all(decoded);
}
